import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Check, X, Clock, Calendar, UserCheck, Loader2, ThumbsUp, ThumbsDown, Repeat, Ban, Users, FileText, BarChart3, RotateCcw, ChevronDown, ChevronRight, Pencil } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_CONFIG = {
  high: { label: 'High', color: 'bg-red-100 text-red-700' },
  medium: { label: 'Medium', color: 'bg-amber-100 text-amber-700' },
  low: { label: 'Low', color: 'bg-green-100 text-green-700' },
};

const REC_LABEL = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' };

const fmtDate = (iso) => iso ? new Date(iso + (iso.length <= 10 ? 'T00:00:00' : '')).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '';
const fmtDateTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

const PortalTeamTodos = () => {
  const [data, setData] = useState({ reports: [], todos: [], pending_approvals: [] });
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);
  const [view, setView] = useState('tasks'); // tasks | recurring
  const [filter, setFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectingTodo, setRejectingTodo] = useState(null);
  const [approvingTodo, setApprovingTodo] = useState(null);
  const [editingTodo, setEditingTodo] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [res, aRes] = await Promise.all([
        axios.get(`${API}/api/employee-portal/team-todos`, { headers }),
        axios.get(`${API}/api/employee-portal/team-todos/analytics`, { headers }).catch(() => ({ data: { analytics: [] } })),
      ]);
      setData(res.data);
      setAnalytics(aRes.data.analytics || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('portal_token');
        navigate('/employee-portal/login');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (todoId, comment = '') => {
    setActionLoading(todoId);
    try {
      await axios.patch(`${API}/api/employee-portal/team-todos/${todoId}/approve`, { comment }, { headers });
      setApprovingTodo(null);
      load();
    } catch (err) { console.error(err); }
    finally { setActionLoading(null); }
  };

  const handleEditSave = async (todoId, payload) => {
    setActionLoading(todoId);
    try {
      await axios.patch(`${API}/api/employee-portal/team-todos/${todoId}/edit`, payload, { headers });
      setEditingTodo(null);
      load();
    } catch (err) { console.error(err); }
    finally { setActionLoading(null); }
  };

  const handleReject = async (todoId, reason = '') => {
    setActionLoading(todoId);
    try {
      await axios.patch(`${API}/api/employee-portal/team-todos/${todoId}/reject`, { reason }, { headers });
      setRejectingTodo(null);
      load();
    } catch (err) { console.error(err); }
    finally { setActionLoading(null); }
  };

  const handleUnapprove = async (todoId) => {
    setActionLoading(todoId);
    try {
      await axios.patch(`${API}/api/employee-portal/team-todos/${todoId}/unapprove`, {}, { headers });
      load();
    } catch (err) { console.error(err); }
    finally { setActionLoading(null); }
  };

  const handleStopRecurrence = async (todoId) => {
    if (!window.confirm('Stop this recurring task for the whole team? Existing tasks stay, but no new ones will be created.')) return;
    setActionLoading(todoId);
    try {
      await axios.patch(`${API}/api/employee-portal/team-todos/${todoId}/stop-recurrence`, {}, { headers });
      load();
    } catch (err) { console.error(err); }
    finally { setActionLoading(null); }
  };

  const allTodos = data.todos || [];
  const pendingApprovals = data.pending_approvals || [];
  const reports = data.reports || [];

  const filtered = allTodos.filter(t => {
    if (filter === 'active') return t.status === 'active';
    if (filter === 'pending') return t.status === 'pending_approval';
    if (filter === 'completed') return t.status === 'completed';
    return true;
  });

  const activeCount = allTodos.filter(t => t.status === 'active').length;
  const pendingCount = pendingApprovals.length;
  const completedCount = allTodos.filter(t => t.status === 'completed').length;

  // Group active recurring tasks into series with a per-cycle completion roster.
  const recurringGroups = useMemo(() => {
    const map = {};
    const rank = (t) => `${t.due_date || ''}|${t.created_at || ''}`;
    allTodos.forEach(t => {
      if (!t.recurrence || t.recurrence === 'none') return;
      const key = t.series_id || `title:${t.title}`;
      if (!map[key]) map[key] = { key, title: t.title, recurrence: t.recurrence, active: false, members: {} };
      const g = map[key];
      if (t.recurrence_active) g.active = true;
      const cur = g.members[t.employee_id];
      if (!cur || rank(t) > rank(cur)) g.members[t.employee_id] = t;
    });
    return Object.values(map).map(g => {
      const members = Object.values(g.members);
      const cycleDate = members.reduce((mx, m) => (m.due_date && m.due_date > mx ? m.due_date : mx), '');
      return { ...g, members, cycleDate };
    });
  }, [allTodos]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" />
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center" data-testid="team-todos-empty">
        <UserCheck className="h-16 w-16 mx-auto mb-4 text-gray-200" />
        <h2 className="text-lg font-bold text-gray-900 mb-2">Team Tasks</h2>
        <p className="text-sm text-gray-400">No direct reports found. Task assignment is available for reporting managers.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="team-todos">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-bold text-gray-900">Team Tasks</h2>
        <Button size="sm" className="bg-violet-600 hover:bg-violet-700" onClick={() => setShowAssign(true)} data-testid="assign-task-btn">
          <Plus className="h-4 w-4 mr-1" /> Assign Task
        </Button>
      </div>

      {/* View switch: Tasks | Recurring */}
      <div className="inline-flex bg-gray-100 rounded-xl p-1" data-testid="team-view-switch">
        <button
          onClick={() => setView('tasks')}
          className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition ${view === 'tasks' ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-500'}`}
          data-testid="view-tasks-btn"
        >
          Tasks
        </button>
        <button
          onClick={() => setView('recurring')}
          className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition flex items-center gap-1 ${view === 'recurring' ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-500'}`}
          data-testid="view-recurring-btn"
        >
          <Repeat className="h-3.5 w-3.5" /> Recurring ({recurringGroups.length})
        </button>
      </div>

      {view === 'recurring' ? (
        <RecurringView
          groups={recurringGroups}
          analytics={analytics}
          actionLoading={actionLoading}
          onStop={handleStopRecurrence}
        />
      ) : (
        <TaskBoard
          todos={allTodos}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          actionLoading={actionLoading}
          onApprove={(todo) => setApprovingTodo(todo)}
          onReject={(todo) => setRejectingTodo(todo)}
          onUnapprove={handleUnapprove}
          onEdit={(todo) => setEditingTodo(todo)}
        />
      )}

      {/* Assign Task Dialog */}
      {showAssign && (
        <AssignTaskDialog
          token={token}
          reports={reports}
          onClose={() => setShowAssign(false)}
          onSuccess={() => { setShowAssign(false); load(); }}
        />
      )}

      {/* Reject-with-reason Dialog */}
      {rejectingTodo && (
        <RejectDialog
          todo={rejectingTodo}
          submitting={actionLoading === rejectingTodo.id}
          onClose={() => setRejectingTodo(null)}
          onConfirm={(reason) => handleReject(rejectingTodo.id, reason)}
        />
      )}

      {/* Approve-with-comment Dialog */}
      {approvingTodo && (
        <ApproveDialog
          todo={approvingTodo}
          submitting={actionLoading === approvingTodo.id}
          onClose={() => setApprovingTodo(null)}
          onConfirm={(comment) => handleApprove(approvingTodo.id, comment)}
        />
      )}

      {/* Edit issued task Dialog */}
      {editingTodo && (
        <EditTaskDialog
          todo={editingTodo}
          submitting={actionLoading === editingTodo.id}
          onClose={() => setEditingTodo(null)}
          onSave={(payload) => handleEditSave(editingTodo.id, payload)}
        />
      )}
    </div>
  );
};

/* ── monday.com-style status board ────────────────── */
const STATUS_BUCKETS = [
  { key: 'overdue',   name: 'Overdue',        bar: 'bg-[#e2445c]', header: 'bg-red-50',     pill: 'bg-[#e2445c]', count: 'bg-[#e2445c]' },
  { key: 'in_review', name: 'In Progress',    bar: 'bg-[#fdab3d]', header: 'bg-amber-50',   pill: 'bg-[#fdab3d]', count: 'bg-[#fdab3d]' },
  { key: 'assigned',  name: 'Assigned',       bar: 'bg-[#579bfc]', header: 'bg-sky-50',     pill: 'bg-[#579bfc]', count: 'bg-[#579bfc]' },
  { key: 'completed', name: 'Completed',      bar: 'bg-[#00c875]', header: 'bg-emerald-50', pill: 'bg-[#00c875]', count: 'bg-[#00c875]' },
];

const bucketOf = (t) => {
  if (t.status === 'completed') return 'completed';
  if (t.status === 'pending_approval') return 'in_review';
  if (t.status === 'active' && t.due_date && new Date(t.due_date + 'T23:59:59') < new Date()) return 'overdue';
  return 'assigned';
};

const TaskBoard = ({ todos, collapsed, setCollapsed, actionLoading, onApprove, onReject, onUnapprove, onEdit }) => {
  const grouped = { overdue: [], in_review: [], assigned: [], completed: [] };
  todos.forEach(t => grouped[bucketOf(t)].push(t));
  const total = todos.length;
  if (total === 0) {
    return (
      <Card className="border-0 shadow-sm" data-testid="team-board-empty">
        <CardContent className="p-8 text-center">
          <UserCheck className="h-12 w-12 mx-auto mb-3 text-gray-200" />
          <p className="text-sm text-gray-400">No tasks assigned yet. Tap “Assign Task” to get started.</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-3" data-testid="team-board">
      {STATUS_BUCKETS.map(b => {
        const items = grouped[b.key];
        if (items.length === 0) return null;
        const isCollapsed = collapsed[b.key];
        return (
          <div key={b.key} data-testid={`board-group-${b.key}`}>
            <button
              onClick={() => setCollapsed(p => ({ ...p, [b.key]: !p[b.key] }))}
              className={`w-full flex items-center gap-2 ${b.header} rounded-lg pl-2 pr-3 py-2.5 border-l-4 ${b.bar.replace('bg-', 'border-')} transition hover:brightness-95`}
              data-testid={`board-group-header-${b.key}`}
            >
              {isCollapsed ? <ChevronRight className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
              <span className="text-sm font-bold text-slate-800">{b.name}</span>
              <span className={`ml-auto ${b.count} text-white text-[11px] font-bold rounded-full min-w-[22px] h-[22px] px-1.5 flex items-center justify-center`}>
                {items.length}
              </span>
            </button>
            {!isCollapsed && (
              <div className="mt-2 overflow-x-auto rounded-lg border border-slate-200 shadow-sm bg-white">
                <table className="w-full min-w-[600px] border-collapse text-left">
                  <thead>
                    <tr className="bg-slate-50 text-[10px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="py-2 px-3">Task</th>
                      <th className="py-2 px-3">Owner</th>
                      <th className="py-2 px-3 text-center">Status</th>
                      <th className="py-2 px-3 text-center">Priority</th>
                      <th className="py-2 px-3">Due</th>
                      <th className="py-2 px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(todo => (
                      <TaskRow key={todo.id} todo={todo} bucket={b} actionLoading={actionLoading}
                        onApprove={onApprove} onReject={onReject} onUnapprove={onUnapprove} onEdit={onEdit} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const PRIORITY_CELL = {
  high: 'bg-[#e2445c] text-white',
  medium: 'bg-[#fdab3d] text-white',
  low: 'bg-[#579bfc] text-white',
};

const TaskRow = ({ todo, bucket, actionLoading, onApprove, onReject, onUnapprove, onEdit }) => {
  const isCompleted = todo.status === 'completed';
  const isPending = todo.status === 'pending_approval';
  const isOverdue = bucket.key === 'overdue';
  const recActive = todo.recurrence && todo.recurrence !== 'none';
  const prio = (todo.priority || 'medium').toLowerCase();
  const atts = (todo.completion_attachments || []).length;
  const initials = (todo.employee_name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  return (
    <tr className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 align-middle" data-testid={`team-todo-${todo.id}`}>
      {/* Task */}
      <td className="py-2 px-3">
        <div className="flex items-start gap-1.5">
          <span className={`w-1 self-stretch rounded ${bucket.bar} flex-shrink-0`} style={{ minHeight: 18 }} />
          <div className="min-w-0">
            <p className={`text-[13px] font-medium leading-tight ${isCompleted ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{todo.title}</p>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              {recActive && (
                <span className="inline-flex items-center gap-0.5 text-[8px] font-semibold text-violet-600">
                  <Repeat className="h-2.5 w-2.5" /> {REC_LABEL[todo.recurrence] || 'Repeats'}
                </span>
              )}
              {(todo.completion_note || atts > 0) && (
                <span className="inline-flex items-center gap-0.5 text-[8px] font-medium text-emerald-600" data-testid={`proof-${todo.id}`}>
                  <FileText className="h-2.5 w-2.5" /> proof{atts > 0 ? ` (${atts})` : ''}
                </span>
              )}
            </div>
          </div>
        </div>
      </td>
      {/* Owner */}
      <td className="py-2 px-3">
        <div className="flex items-center gap-1.5">
          <span className="h-6 w-6 rounded-full bg-violet-100 text-violet-700 text-[9px] font-bold flex items-center justify-center flex-shrink-0">{initials}</span>
          <span className="text-[11px] text-slate-600 whitespace-nowrap">{todo.employee_name}</span>
        </div>
      </td>
      {/* Status (filled cell) */}
      <td className="py-1 px-1 text-center">
        <div className={`${bucket.pill} text-white text-[11px] font-semibold py-2 px-2 rounded-[4px]`}>{bucket.name}</div>
      </td>
      {/* Priority (filled cell) */}
      <td className="py-1 px-1 text-center">
        <div className={`${PRIORITY_CELL[prio] || PRIORITY_CELL.medium} text-[11px] font-semibold py-2 px-2 rounded-[4px] capitalize`}>{prio}</div>
      </td>
      {/* Due / timeline pill */}
      <td className="py-2 px-3">
        {todo.due_date ? (
          <span className={`inline-block text-[10px] font-semibold text-white px-2 py-1 rounded-full whitespace-nowrap ${isOverdue ? 'bg-[#e2445c]' : 'bg-slate-700'}`}>
            {fmtDate(todo.due_date)}
          </span>
        ) : <span className="text-[10px] text-slate-300">—</span>}
      </td>
      {/* Actions */}
      <td className="py-2 px-2">
        {isPending && (
          <div className="flex gap-1 justify-end">
            <button onClick={() => onApprove(todo)} disabled={actionLoading === todo.id} title="Approve"
              className="h-7 w-7 rounded-md bg-[#00c875] hover:bg-emerald-600 text-white flex items-center justify-center disabled:opacity-50" data-testid={`approve-inline-${todo.id}`}>
              {actionLoading === todo.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ThumbsUp className="h-3.5 w-3.5" />}
            </button>
            <button onClick={() => onReject(todo)} disabled={actionLoading === todo.id} title="Reject"
              className="h-7 w-7 rounded-md border border-red-200 text-red-600 hover:bg-red-50 flex items-center justify-center disabled:opacity-50" data-testid={`reject-inline-${todo.id}`}>
              <ThumbsDown className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        {!isCompleted && !isPending && (
          <div className="flex justify-end">
            <button onClick={() => onEdit(todo)} title="Edit task"
              className="h-7 w-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-100 flex items-center justify-center" data-testid={`edit-todo-${todo.id}`}>
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        {isCompleted && (
          <div className="flex justify-end">
            <button onClick={() => onUnapprove(todo.id)} disabled={actionLoading === todo.id} title="Undo approval"
              className="h-7 w-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-100 flex items-center justify-center disabled:opacity-50" data-testid={`unapprove-${todo.id}`}>
              {actionLoading === todo.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
            </button>
          </div>
        )}
      </td>
    </tr>
  );
};

/* ── Proof of completion (note + photo/file) ────────────────── */
const ProofBlock = ({ todo }) => {
  const note = todo.completion_note;
  const atts = todo.completion_attachments || [];
  if (!note && atts.length === 0) return null;
  return (
    <div className="mt-2 bg-emerald-50 border border-emerald-100 rounded-lg p-2" data-testid={`proof-${todo.id}`}>
      <p className="text-[9px] font-semibold text-emerald-700 uppercase tracking-wide mb-1">Proof of completion</p>
      {note && <p className="text-[11px] text-gray-600 whitespace-pre-wrap">{note}</p>}
      {atts.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {atts.map(a => (
            <a key={a.id} href={`${API}/api/employee-portal/team-todos/proof/${a.id}`} target="_blank" rel="noreferrer" data-testid={`proof-att-${a.id}`}>
              {a.kind === 'image' ? (
                <img src={`${API}/api/employee-portal/team-todos/proof/${a.id}`} alt={a.name} className="h-14 w-14 rounded-md object-cover border hover:ring-2 hover:ring-emerald-300 transition" />
              ) : (
                <div className="h-14 w-14 rounded-md border bg-white flex flex-col items-center justify-center text-gray-400 hover:ring-2 hover:ring-emerald-300 transition">
                  <FileText className="h-4 w-4" />
                  <span className="text-[8px] mt-0.5">{(a.name || '').split('.').pop()}</span>
                </div>
              )}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Recurring completion view ────────────────── */
const RecurringView = ({ groups, analytics = [], actionLoading, onStop }) => {
  const barColor = (rate) => rate >= 80 ? 'bg-emerald-500' : rate >= 50 ? 'bg-amber-500' : 'bg-red-500';
  const AnalyticsCard = analytics.length > 0 ? (
    <Card className="border-0 shadow-sm" data-testid="recurring-analytics">
      <CardContent className="p-3">
        <div className="flex items-center gap-1.5 mb-2.5">
          <BarChart3 className="h-4 w-4 text-violet-600" />
          <p className="text-sm font-semibold text-gray-900">Completion Rate</p>
          <span className="text-[10px] text-gray-400">· worst first</span>
        </div>
        <div className="space-y-2.5">
          {analytics.map(a => (
            <div key={a.employee_id} data-testid={`analytics-row-${a.employee_code || a.employee_id}`}>
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-xs text-gray-700 truncate mr-2">{a.name}</span>
                <span className={`text-[11px] font-semibold ${a.rate >= 80 ? 'text-emerald-600' : a.rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                  {a.done}/{a.total} · {a.rate}%
                </span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${barColor(a.rate)}`} style={{ width: `${a.rate}%` }} />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  ) : null;

  if (groups.length === 0) {
    return (
      <div className="space-y-3">
        {AnalyticsCard}
        <Card className="border-0 shadow-sm" data-testid="recurring-empty">
          <CardContent className="p-8 text-center">
            <Repeat className="h-12 w-12 mx-auto mb-3 text-gray-200" />
            <p className="text-sm text-gray-400">No active recurring tasks. Assign a task with a repeat schedule to track it here.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="recurring-view">
      {AnalyticsCard}
      {groups.map(g => {
        const done = g.members.filter(m => m.status === 'completed').length;
        const anyId = g.members[0]?.id;
        return (
          <Card key={g.key} className="border-0 shadow-sm" data-testid={`recurring-group-${g.key}`}>
            <CardContent className="p-3">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{g.title}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <Badge className="text-[9px] px-1.5 py-0 bg-violet-100 text-violet-700">
                      <Repeat className="h-2.5 w-2.5 mr-0.5" /> {REC_LABEL[g.recurrence] || 'Repeats'}
                    </Badge>
                    {g.cycleDate && (
                      <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                        <Calendar className="h-2.5 w-2.5" /> Cycle {fmtDate(g.cycleDate)}
                      </span>
                    )}
                    <span className="text-[10px] font-semibold text-emerald-600" data-testid={`recurring-progress-${g.key}`}>
                      {done}/{g.members.length} done
                    </span>
                  </div>
                </div>
                {g.active && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 border-red-200 text-red-600 hover:bg-red-50 flex-shrink-0"
                    onClick={() => onStop(anyId)}
                    disabled={actionLoading === anyId}
                    data-testid={`stop-recurrence-${g.key}`}
                  >
                    {actionLoading === anyId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Ban className="h-3.5 w-3.5 mr-1" /> Stop</>}
                  </Button>
                )}
              </div>
              <div className="divide-y">
                {g.members.map(m => {
                  const isCompleted = m.status === 'completed';
                  const isPending = m.status === 'pending_approval';
                  const isOverdue = m.due_date && m.status === 'active' && new Date(m.due_date + 'T23:59:59') < new Date();
                  return (
                    <div key={m.id} className="flex items-center justify-between py-1.5" data-testid={`recurring-member-${m.employee_code || m.employee_id}`}>
                      <span className="text-xs text-gray-700 truncate mr-2">{m.employee_name}</span>
                      {isCompleted ? (
                        <span className="flex items-center gap-1 text-[10px] font-medium text-emerald-600"><Check className="h-3 w-3" /> Done</span>
                      ) : isPending ? (
                        <span className="flex items-center gap-1 text-[10px] font-medium text-amber-600"><Clock className="h-3 w-3" /> Awaiting approval</span>
                      ) : (
                        <span className={`flex items-center gap-1 text-[10px] font-medium ${isOverdue ? 'text-red-500' : 'text-gray-400'}`}>
                          <Clock className="h-3 w-3" /> {isOverdue ? 'Overdue' : 'Pending'}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

/* ── Reject with reason dialog ────────────────── */
const RejectDialog = ({ todo, submitting, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center" data-testid="reject-dialog">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl z-10">
        <div className="border-b px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <h3 className="text-base font-bold text-gray-900">Send Task Back</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100"><X className="h-5 w-5 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-gray-50 rounded-xl px-3 py-2.5">
            <p className="text-sm font-medium text-gray-900">{todo.title}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">{todo.employee_name}</p>
          </div>
          <div>
            <Label className="text-sm font-medium">Reason <span className="text-gray-400 font-normal">(optional)</span></Label>
            <Textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Please redo the deep-clean under the fryer and re-upload a photo."
              rows={3}
              className="mt-1"
              data-testid="reject-reason-input"
            />
            <p className="text-[11px] text-gray-400 mt-1">The staff member will see this note so they know exactly what to fix.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onClose} disabled={submitting} data-testid="reject-cancel-btn">Cancel</Button>
            <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={() => onConfirm(reason.trim())} disabled={submitting} data-testid="reject-confirm-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ThumbsDown className="h-4 w-4 mr-1.5" /> Send Back</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Approve with comment dialog ────────────────── */
const ApproveDialog = ({ todo, submitting, onClose, onConfirm }) => {
  const [comment, setComment] = useState('');
  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center" data-testid="approve-dialog">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl z-10">
        <div className="border-b px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <h3 className="text-base font-bold text-gray-900">Approve Task</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100"><X className="h-5 w-5 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-gray-50 rounded-xl px-3 py-2.5">
            <p className="text-sm font-medium text-gray-900">{todo.title}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">{todo.employee_name}</p>
          </div>
          <ProofBlock todo={todo} />
          <div>
            <Label className="text-sm font-medium">Comment <span className="text-gray-400 font-normal">(optional)</span></Label>
            <Textarea value={comment} onChange={e => setComment(e.target.value)} rows={3} className="mt-1"
              placeholder="e.g. Great job — well done." data-testid="approve-comment-input" />
            <p className="text-[11px] text-gray-400 mt-1">The staff member will see this note with the approval.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onClose} disabled={submitting} data-testid="approve-cancel-btn">Cancel</Button>
            <Button className="flex-1 bg-[#00c875] hover:bg-emerald-600 text-white" onClick={() => onConfirm(comment.trim())} disabled={submitting} data-testid="approve-confirm-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ThumbsUp className="h-4 w-4 mr-1.5" /> Approve</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Edit issued task dialog ────────────────── */
const EditTaskDialog = ({ todo, submitting, onClose, onSave }) => {
  const [title, setTitle] = useState(todo.title || '');
  const [description, setDescription] = useState(todo.description || '');
  const [dueDate, setDueDate] = useState(todo.due_date || '');
  const [priority, setPriority] = useState(todo.priority || 'medium');
  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center" data-testid="edit-task-dialog">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl z-10 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <h3 className="text-base font-bold text-gray-900">Edit Task</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100"><X className="h-5 w-5 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <Label className="text-sm font-medium">Title *</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} className="mt-1" data-testid="edit-title-input" />
          </div>
          <div>
            <Label className="text-sm font-medium">Description</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className="mt-1" data-testid="edit-desc-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium">Due Date</Label>
              <Input type="date" value={dueDate || ''} onChange={e => setDueDate(e.target.value)} className="mt-1" data-testid="edit-due-input" />
            </div>
            <div>
              <Label className="text-sm font-medium">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="mt-1" data-testid="edit-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent className="z-[100]">
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button variant="outline" className="flex-1" onClick={onClose} disabled={submitting} data-testid="edit-cancel-btn">Cancel</Button>
            <Button className="flex-1 bg-violet-600 hover:bg-violet-700" disabled={submitting || !title.trim()}
              onClick={() => onSave({ title: title.trim(), description, due_date: dueDate || '', priority })} data-testid="edit-save-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1.5" /> Save Changes</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Assign Task Dialog ────────────────── */
const AssignTaskDialog = ({ token, reports, onClose, onSuccess }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('medium');
  const [recurrence, setRecurrence] = useState('none');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const departments = useMemo(
    () => [...new Set(reports.map(r => r.department).filter(Boolean))].sort(),
    [reports]
  );

  const allSelected = reports.length > 0 && selectedIds.length === reports.length;
  const toggleAll = () => setSelectedIds(allSelected ? [] : reports.map(r => r.id));
  const toggleOne = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const deptIds = (dept) => reports.filter(r => r.department === dept).map(r => r.id);
  const deptAllSelected = (dept) => {
    const ids = deptIds(dept);
    return ids.length > 0 && ids.every(id => selectedIds.includes(id));
  };
  const toggleDept = (dept) => {
    const ids = deptIds(dept);
    setSelectedIds(prev => deptAllSelected(dept)
      ? prev.filter(id => !ids.includes(id))
      : [...new Set([...prev, ...ids])]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedIds.length === 0) { setError('Select at least one team member'); return; }
    if (!title.trim()) { setError('Title is required'); return; }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/api/employee-portal/team-todos`, {
        employee_ids: selectedIds,
        title: title.trim(),
        description,
        due_date: dueDate || null,
        priority,
        recurrence,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to assign');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="assign-task-dialog">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto z-10">
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h3 className="text-base font-bold text-gray-900">Assign Task</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100"><X className="h-5 w-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-sm font-medium">Assign To *</Label>
              <button
                type="button"
                onClick={toggleAll}
                className="text-xs font-semibold text-violet-600 hover:text-violet-700"
                data-testid="assign-select-all"
              >
                {allSelected ? 'Clear all' : 'Select whole team'}
              </button>
            </div>
            {departments.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="assign-dept-chips">
                {departments.map(dept => {
                  const on = deptAllSelected(dept);
                  return (
                    <button
                      key={dept}
                      type="button"
                      onClick={() => toggleDept(dept)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border transition ${on ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-200 hover:border-violet-300'}`}
                      data-testid={`assign-dept-${dept}`}
                    >
                      <Users className="h-3 w-3" /> {dept}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="border rounded-lg divide-y max-h-52 overflow-y-auto" data-testid="assign-team-list">
              {reports.map(r => {
                const checked = selectedIds.includes(r.id);
                return (
                  <label
                    key={r.id}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer ${checked ? 'bg-violet-50' : 'hover:bg-gray-50'}`}
                    data-testid={`assign-member-${r.employee_code || r.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleOne(r.id)}
                      className="h-4 w-4 accent-violet-600"
                      data-testid={`assign-check-${r.employee_code || r.id}`}
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-gray-900 truncate">{r.name}</span>
                      <span className="block text-[11px] text-gray-400">{r.employee_code}{r.department ? ` · ${r.department}` : ''}</span>
                    </span>
                  </label>
                );
              })}
            </div>
            <p className="text-[11px] text-gray-400 mt-1" data-testid="assign-selected-count">
              {selectedIds.length} of {reports.length} selected
            </p>
          </div>
          <div>
            <Label className="text-sm font-medium">Title *</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Task title" className="mt-1" data-testid="assign-title-input" />
          </div>
          <div>
            <Label className="text-sm font-medium">Description</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Details..." rows={2} className="mt-1" data-testid="assign-desc-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium">{recurrence === 'none' ? 'Due Date' : 'Start Date'}</Label>
              <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="mt-1" data-testid="assign-due-input" />
            </div>
            <div>
              <Label className="text-sm font-medium">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="mt-1" data-testid="assign-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium">Repeat</Label>
            <Select value={recurrence} onValueChange={setRecurrence}>
              <SelectTrigger className="mt-1" data-testid="assign-recurrence-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[100]">
                <SelectItem value="none">Does not repeat</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
            {recurrence !== 'none' && (
              <p className="text-[11px] text-violet-600 mt-1">A fresh task will be created {recurrence} automatically. Staff get a reminder at 08:00 the morning it's due.</p>
            )}
          </div>
          {error && <p className="text-sm text-red-500 text-center">{error}</p>}
          <Button type="submit" className="w-full py-5 bg-violet-600 hover:bg-violet-700" disabled={submitting || !title.trim() || selectedIds.length === 0} data-testid="submit-assign-btn">
            {submitting ? 'Assigning...' : <><Plus className="h-4 w-4 mr-2" /> Assign Task{selectedIds.length > 1 ? ` (${selectedIds.length})` : ''}</>}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PortalTeamTodos;
